import { Link } from "react-router-dom";
import { UploadCloud, CalendarClock, Send, Sparkles, BarChart3, DollarSign, Share2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const STEPS = [
  { icon: CalendarClock, t: "Auto-Scheduling", d: "Upload 30 posts, pick 'one every day at 8 PM' — we publish each on time." },
  { icon: Send, t: "Auto-Notifications", d: "Subscribers get emails and push notifications the moment you go live." },
  { icon: Sparkles, t: "Auto-Optimization", d: "Every photo and video is optimized automatically for quality and speed." },
  { icon: BarChart3, t: "Auto-Analytics", d: "Views, likes and engagement update in real time — no spreadsheets." },
  { icon: DollarSign, t: "Auto-Earnings", d: "Earnings and payouts update automatically as your content performs." },
  { icon: Share2, t: "Auto-Referrals", d: "Referral tracking runs in the background, crediting every signup." },
];

export default function AutoPilot() {
  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 text-center" data-testid="autopilot-hero">
        <Reveal>
          <p className="overline text-gold mb-4">The Fan Focus Advantage</p>
          <h1 className="font-display text-6xl sm:text-7xl md:text-8xl uppercase text-white leading-[0.9]">
            Upload.<br /><span className="text-gold">We Handle The Rest.</span>
          </h1>
          <p className="mt-6 text-white/65 text-lg max-w-2xl mx-auto">
            Most platforms give you more work. Fan Focus removes it. AutoPilot Mode turns a single upload into a fully managed, automatically published, automatically monetized content engine.
          </p>
          <div className="mt-9 flex flex-wrap gap-4 justify-center">
            <Link to="/register" data-testid="autopilot-start" className="inline-flex items-center gap-2 bg-gold text-black px-8 py-4 overline hover:bg-[#F5D061] transition-colors">
              Try AutoPilot <ArrowRight size={16} />
            </Link>
            <Link to="/dashboard" data-testid="autopilot-demo" className="inline-flex items-center border border-white/25 text-white px-8 py-4 overline hover:border-gold hover:text-gold transition-colors">
              See The Dashboard
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20">
        <div className="border border-white/10 bg-[#0a0a0a] p-8 md:p-12" data-testid="autopilot-flow">
          {["A creator uploads 30 photos or videos at once.",
            "They choose \u201cPost one every day at 8 PM.\u201d",
            "Fan Focus publishes the content automatically.",
            "Subscribers are notified automatically.",
            "Media is optimized, analytics & earnings update — automatically."].map((line, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex items-start gap-5 py-4 border-b border-white/10 last:border-0">
                <span className="font-display text-3xl text-gold/80 leading-none w-10">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-white/80 text-lg leading-relaxed">{line}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={["Upload", "Automate", "Publish", "Notify", "Earn", "Repeat"]} />

      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal className="text-center mb-14">
          <h2 className="font-display text-4xl md:text-6xl uppercase text-white">Everything, <span className="text-gold">Automatic</span></h2>
          <p className="mt-4 text-white/60">Your only job is to create. We take care of the rest behind the scenes.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {STEPS.map((s, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className="bg-[#0a0a0a]">
              <div className="p-9 h-full" data-testid={`auto-feature-${i}`}>
                <s.icon size={34} strokeWidth={1.2} className="text-gold mb-5" />
                <h3 className="font-cond uppercase tracking-wide text-xl text-white mb-2">{s.t}</h3>
                <p className="text-white/55 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-28 text-center">
        <Reveal>
          <UploadCloud size={44} className="text-gold mx-auto mb-6" strokeWidth={1.2} />
          <h2 className="font-display text-5xl md:text-7xl uppercase text-white leading-[0.9]">
            Spend Less Time Managing.<br /><span className="text-gold">More Time Earning.</span>
          </h2>
          <Link to="/register" className="mt-9 inline-flex items-center gap-2 bg-gold text-black px-10 py-4 overline hover:bg-[#F5D061] transition-colors">
            Start On AutoPilot <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
